import { z } from 'zod';
import { BusinessCodeSchema, EnteredNameSchema, RecordIdSchema } from './identifiers';
import { MoneySchema, divideMoney, multiplyMoney, roundMoney, type Money } from './money';
import { InstantSchema } from './time';

/**
 * Payroll — BMP-2 package 7 (`HR-PAY-001…004`, `HR-ADV-001`, `HR-COMM-001`).
 *
 * **No statutory calculation is built in** (`BD-44`): social insurance, income tax and every other
 * component is configured by an authorized person as a fixed amount or a percentage of the basic
 * salary. The cycle (`BD-43`) is one run per legal entity and month. Commission rules are `BD-14`:
 * only commission lines someone recorded and someone else approved enter a run.
 *
 * A run is prepared (computed, stored as lines), submitted, approved by someone other than its
 * preparer (or through the engine where a policy governs `hr.payroll.approve`), then posted — once —
 * which records `payroll.posted`, takes the advance repayments and the commission lines it used, and
 * locks it. Nothing posts before approval. A posted run is corrected only by a reversing run.
 *
 * The bank-transfer file of an approved run is exported; the product never executes the transfer.
 * Employee bank details are encrypted and masked; an employee sees only their own payslips.
 */

export const COMPONENT_KINDS = ['earning', 'deduction'] as const;
export const COMPONENT_BASES = ['fixed', 'percentOfBasic'] as const;

export const PayComponentSchema = z.strictObject({
  componentId: RecordIdSchema,
  code: BusinessCodeSchema,
  name: EnteredNameSchema,
  legalEntityId: RecordIdSchema,
  kind: z.enum(COMPONENT_KINDS),
  basis: z.enum(COMPONENT_BASES),
  /** Shown only: no statutory meaning is assumed (BD-44). */
  note: z.string().optional(),
  state: z.enum(['active', 'inactive']),
  version: z.number().int().positive(),
});
export type PayComponent = z.infer<typeof PayComponentSchema>;
export const CreatePayComponentSchema = z.strictObject({
  code: BusinessCodeSchema,
  name: EnteredNameSchema,
  legalEntityId: RecordIdSchema,
  kind: z.enum(COMPONENT_KINDS),
  basis: z.enum(COMPONENT_BASES),
  note: z.string().trim().min(2).max(300).optional(),
});

const Percent = z
  .string()
  .regex(/^\d{1,2}(\.\d{1,4})?$/, { message: 'PERCENT_EXPECTED' })
  .refine((value) => Number(value) > 0, { message: 'PERCENT_EXPECTED' });

export const SalaryLineSchema = z.strictObject({
  componentId: RecordIdSchema,
  /** A fixed component's monthly amount. */
  amount: MoneySchema.optional(),
  /** A percent-of-basic component's rate. */
  percent: Percent.optional(),
});

/** An employee's salary from a date; a change is a new structure, never an edit. */
export const SalaryStructureSchema = z.strictObject({
  structureId: RecordIdSchema,
  employeeId: RecordIdSchema,
  effectiveFrom: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  basic: MoneySchema,
  lines: z.array(SalaryLineSchema),
  bank: z.strictObject({ bankName: z.string(), accountMasked: z.string() }).optional(),
  recordedByAccountId: z.string(),
  createdAt: InstantSchema,
});
export type SalaryStructure = z.infer<typeof SalaryStructureSchema>;

export const SetSalarySchema = z.strictObject({
  employeeId: RecordIdSchema,
  effectiveFrom: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, { message: 'PERIOD_EXPECTED' }),
  basic: MoneySchema,
  lines: z.array(SalaryLineSchema).max(40).default([]),
  bank: z
    .strictObject({
      bankName: z.string().trim().min(2).max(120),
      /** IBAN or account number; encrypted at rest, shown masked. */
      account: z.string().trim().min(6).max(40),
    })
    .optional(),
  reason: z.string().trim().min(3).max(300),
});

/* ------------------------------------------------------------- advances */

export const ADVANCE_STATES = ['requested', 'paid', 'settled', 'cancelled'] as const;
export const EmployeeAdvanceSchema = z.strictObject({
  advanceId: RecordIdSchema,
  employeeId: RecordIdSchema,
  amount: MoneySchema,
  /** Deducted from each payroll run until repaid. */
  monthlyRepayment: MoneySchema,
  repaid: MoneySchema,
  outstanding: MoneySchema,
  state: z.enum(ADVANCE_STATES),
  paymentRequestId: z.string().optional(),
  reason: z.string(),
  requestedByAccountId: z.string(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
});
export type EmployeeAdvance = z.infer<typeof EmployeeAdvanceSchema>;
export const RequestEmployeeAdvanceSchema = z.strictObject({
  employeeId: RecordIdSchema,
  amount: MoneySchema,
  monthlyRepayment: MoneySchema,
  fromAccountId: RecordIdSchema,
  method: z.enum(['bankTransfer', 'cash', 'cheque']),
  reason: z.string().trim().min(3).max(300),
  idempotencyKey: z.string().trim().min(8).max(200),
});

/* ----------------------------------------------------------- commission */

export const COMMISSION_STATES = ['recorded', 'approved', 'rejected', 'paid'] as const;
export const CommissionLineSchema = z.strictObject({
  lineId: RecordIdSchema,
  employeeId: RecordIdSchema,
  period: z.string(),
  amount: MoneySchema,
  /** What it is for: a contract number, a campaign — free text; the rules are BD-14. */
  reference: z.string(),
  state: z.enum(COMMISSION_STATES),
  recordedByAccountId: z.string(),
  decidedByAccountId: z.string().optional(),
  runId: z.string().optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
});
export type CommissionLine = z.infer<typeof CommissionLineSchema>;
export const RecordCommissionSchema = z.strictObject({
  employeeId: RecordIdSchema,
  period: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, { message: 'PERIOD_EXPECTED' }),
  amount: MoneySchema,
  reference: z.string().trim().min(2).max(200),
  idempotencyKey: z.string().trim().min(8).max(200),
});
export const DecideCommissionSchema = z.strictObject({
  decision: z.enum(['approve', 'reject']),
  expectedVersion: z.number().int().positive(),
});

/* ------------------------------------------------------------------ runs */

export const PAYROLL_RUN_STATES = [
  'prepared',
  'submitted',
  'approved',
  'posted',
  'discarded',
  'reversed',
] as const;

export const PayslipLineSchema = z.strictObject({
  code: z.string(),
  name: z.string(),
  kind: z.enum(['earning', 'deduction']),
  amount: MoneySchema,
});
export const PayrollLineSchema = z.strictObject({
  employeeId: RecordIdSchema,
  number: z.string(),
  name: z.string(),
  basic: MoneySchema,
  items: z.array(PayslipLineSchema),
  gross: MoneySchema,
  deductions: MoneySchema,
  net: MoneySchema,
  bankMasked: z.string().optional(),
});
export type PayrollLine = z.infer<typeof PayrollLineSchema>;

export const PayrollRunSchema = z.strictObject({
  runId: RecordIdSchema,
  legalEntityId: RecordIdSchema,
  period: z.string(),
  currency: z.string(),
  state: z.enum(PAYROLL_RUN_STATES),
  lines: z.array(PayrollLineSchema),
  gross: MoneySchema,
  deductions: MoneySchema,
  net: MoneySchema,
  /** Active employees left out because no salary structure applies to the month. */
  withoutSalary: z.number().int().nonnegative(),
  /** A reversing run names the run it reverses. */
  reverses: z.string().optional(),
  preparedByAccountId: z.string(),
  approvedByAccountId: z.string().optional(),
  postedByAccountId: z.string().optional(),
  approvalRequestId: z.string().optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
});
export type PayrollRun = z.infer<typeof PayrollRunSchema>;

export const PreparePayrollSchema = z.strictObject({
  legalEntityId: RecordIdSchema,
  period: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, { message: 'PERIOD_EXPECTED' }),
  currency: z.string().regex(/^[A-Z]{3}$/),
});
export const PayrollActionSchema = z.strictObject({
  expectedVersion: z.number().int().positive(),
  reason: z.string().trim().min(3).max(300).optional(),
});
export const PayslipSchema = z.strictObject({
  runId: RecordIdSchema,
  period: z.string(),
  line: PayrollLineSchema,
  postedAt: InstantSchema.optional(),
});
export type Payslip = z.infer<typeof PayslipSchema>;

/** A component's amount for a basic salary: fixed as given, or a percentage half-up to two places. */
export function componentAmount(
  basic: Money,
  line: { amount?: Money | undefined; percent?: string | undefined },
): Money {
  if (line.amount) return line.amount;
  return roundMoney(
    divideMoney(multiplyMoney(basic, line.percent ?? '0'), '100', 10, 'halfUp'),
    2,
    'halfUp',
  );
}

export const PAYROLL_APPROVAL_OPERATIONS = {
  run: 'hr.payroll.approve',
} as const;

export const PAYROLL_AUDIT_ACTIONS = {
  componentCreated: 'hr.payroll.componentCreated',
  salarySet: 'hr.payroll.salarySet',
  advanceRequested: 'hr.payroll.advanceRequested',
  advanceSettled: 'hr.payroll.advancePaid',
  commissionRecorded: 'hr.payroll.commissionRecorded',
  commissionDecided: 'hr.payroll.commissionDecided',
  runPrepared: 'hr.payroll.runPrepared',
  runStateChanged: 'hr.payroll.runStateChanged',
  runExported: 'hr.payroll.bankFileExported',
} as const;
